import { computed, signal } from '@angular/core';
import { CapabilityTypeDto } from '../shared/services/points-api.service';

/**
 * Capability Types State
 * Global state for capability type definitions
 * Source of truth: Backend API /api/v1/capabilities
 */

export interface CapabilityTypesState {
  types: CapabilityTypeDto[];
  loading: boolean;
  loaded: boolean;
  error: string | null;
}

const initialState: CapabilityTypesState = {
  types: [],
  loading: false,
  loaded: false,
  error: null
};

// Private state signal
const _state = signal<CapabilityTypesState>(initialState);

/**
 * Capability Types State Manager
 * Manages the global state for capability type definitions
 */
class CapabilityTypesStateManager {
  // Computed selectors
  readonly types = computed(() => _state().types);
  readonly loading = computed(() => _state().loading);
  readonly loaded = computed(() => _state().loaded);
  readonly error = computed(() => _state().error);

  /**
   * Get all capability types as an array
   */
  readonly allTypes = computed(() => _state().types);

  /**
   * Get capability type by ID
   */
  getTypeById(id: string): CapabilityTypeDto | undefined {
    return _state().types.find(t => t.id === id);
  }

  /**
   * Get capability type by code
   */
  getTypeByCode(code: string): CapabilityTypeDto | undefined {
    return _state().types.find(t => t.code === code);
  }

  /**
   * Get capability type display name by ID or code
   */
  getTypeName(idOrCode: string | undefined): string {
    if (!idOrCode) return '';
    const type = this.getTypeById(idOrCode) || this.getTypeByCode(idOrCode);
    return type?.name || idOrCode;
  }

  /**
   * Get capability type icon by ID or code
   */
  getTypeIcon(idOrCode: string | undefined): string {
    if (!idOrCode) return '';
    const type = this.getTypeById(idOrCode) || this.getTypeByCode(idOrCode);
    return type?.icon || '';
  }

  /**
   * Set loading state
   */
  setLoading(loading: boolean): void {
    _state.update(state => ({ ...state, loading }));
  }

  /**
   * Set types from API response
   */
  setTypes(types: CapabilityTypeDto[]): void {
    console.log('📦 CapabilityTypesState: Setting types from API:', types);
    _state.update(state => ({
      ...state,
      types,
      loading: false,
      loaded: true,
      error: null
    }));
  }

  /**
   * Set error state
   */
  setError(error: string): void {
    console.error('❌ CapabilityTypesState: Error:', error);
    _state.update(state => ({
      ...state,
      loading: false,
      error
    }));
  }

  /**
   * Check if types need to be loaded
   */
  needsLoad(): boolean {
    const state = _state();
    return !state.loaded && !state.loading;
  }

  /**
   * Reset state to initial
   */
  reset(): void {
    _state.set(initialState);
  }
}

// Export singleton instance
export const capabilityTypesState = new CapabilityTypesStateManager();
